/**
 * Catálogos de estado — los datos que el StatusBadge pinta.
 *
 * Viven aparte del componente porque son DATOS: mezclarlos rompe el fast
 * refresh de Vite, y además los consume código que no renderiza nada (un filtro
 * que necesita la lista de estados, por ejemplo).
 *
 * 🔴 Las claves son las del REPO, no las del design system. Tres no coincidían:
 *
 *     sistema            repo (SaleDocument.STATUS_CHOICES)
 *     sent           →   sent_facturador
 *     querying       →   querying_status
 *     accepted_repairs   no existe
 *
 * Con las del sistema, un documento «Enviado» habría mostrado la cadena
 * `sent_facturador` en la tabla: el componente cae a la clave cuando no
 * encuentra la entrada. Es el mismo fallo del desglose de la reforma de
 * pensiones, donde `crp_rate` salió crudo por una etiqueta que faltaba.
 *
 * ⚠️ Los RÓTULOS también son los del producto. El sistema propone «Enviado al
 * facturador», «Consultando estado», «Aceptado SII» y «Pago parcial»; las
 * pantallas dicen otra cosa desde siempre y coinciden entre sí. Lo que sí cambia
 * con el port es el COLOR: diez paletas sueltas pasan a cinco tonos semánticos.
 */
export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger'

export interface StatusEntry {
  label: string
  tone: StatusTone
  /** El punto late. Reservado a lo transitorio: algo que va a cambiar solo. */
  live?: boolean
}

export type StatusCatalog = Record<string, StatusEntry>

/**
 * Ciclo del DTE. Las diez claves son las de `SaleDocument.STATUS_CHOICES`.
 *
 * ⚠️ Los cinco intermedios son pasos del pipeline y **laten**: quien mira la
 * tabla tiene que entender que ese documento se está moviendo sin que él haga
 * nada, y que conviene volver a mirar. `accepted` y `rejected` no laten porque
 * son definitivos.
 */
export const DTE_STATUS: StatusCatalog = {
  draft: { label: 'Borrador', tone: 'neutral' },
  processing: { label: 'Procesando', tone: 'warning', live: true },
  xml_generated: { label: 'XML Generado', tone: 'info', live: true },
  xml_signed: { label: 'XML Firmado', tone: 'info', live: true },
  pending_send: { label: 'Por Enviar', tone: 'warning', live: true },
  sent_facturador: { label: 'Enviado', tone: 'info', live: true },
  querying_status: { label: 'Consultando', tone: 'info', live: true },
  accepted: { label: 'Aceptado', tone: 'success' },
  rejected: { label: 'Rechazado', tone: 'danger' },
  cancelled: { label: 'Anulado', tone: 'neutral' },
}

/**
 * Cobranza. Las claves son las de `SaleDocument.COLLECTION_STATUS_CHOICES`.
 *
 * ⚠️ `paid_historical` no está en el sistema y en el repo es central: marca la
 * deuda que ya venía cobrada de antes de la migración —confundirla con deuda
 * viva es lo que infló el aging en $13.251 millones hasta el #1039—. La
 * distinción la lleva el **rótulo**, no el tono: va en verde como cualquier
 * documento saldado, que es la decisión que `SupplierPaymentsPage` ya había
 * tomado y dejado escrita tras un bug donde caía en «Pendiente» y se leía como
 * deuda por cobrar.
 */
export const COLLECTION_STATUS: StatusCatalog = {
  pending: { label: 'Pendiente', tone: 'neutral' },
  partial: { label: 'Parcial', tone: 'info' },
  paid: { label: 'Pagada', tone: 'success' },
  overdue: { label: 'Vencida', tone: 'danger' },
  paid_historical: { label: 'Pagada (histórica)', tone: 'success' },
  // La cobra el sistema de origen (el ERP que emite por el agente local): no es deuda en Clobi.
  external: { label: 'En el sistema de origen', tone: 'neutral' },
}

/**
 * El estado de un cobro de cliente.
 *
 * Las tres claves salen de `CustomerPayment.PAY_STATUS_CHOICES`, verificadas
 * contra el modelo. ⚠️ `registered` es `warning` y no `neutral`: un cobro
 * registrado y sin confirmar es plata que la contabilidad todavía no reconoce,
 * o sea algo que espera una acción — no un estado en reposo.
 */
export const PAYMENT_STATUS: StatusCatalog = {
  registered: { label: 'Registrado', tone: 'warning' },
  confirmed: { label: 'Confirmado', tone: 'success' },
  cancelled: { label: 'Anulado', tone: 'danger' },
}

/**
 * Períodos de nómina. Las cinco claves de `PayrollPeriod.STATUS_CHOICES`.
 *
 * 🔴 **No es el mismo catálogo que el de la liquidación, aunque se parezcan.**
 * El período tiene `closed` y la liquidación no: son dos modelos distintos, y
 * un catálogo único ofrecería a una liquidación un estado que su `status` nunca
 * puede tomar. La primera versión de este archivo los mezcló en uno solo — con
 * `closed` adentro y los rótulos en femenino («Calculada», «Aprobada») contra el
 * masculino que usan el modelo y las dos pantallas desde siempre.
 *
 * ⚠️ Los rótulos salen de `STATUS_CHOICES`, que es la fuente de verdad, y
 * coinciden con lo que `PeriodsTab` y `PayslipsTab` ya muestran.
 */
export const PAYROLL_PERIOD_STATUS: StatusCatalog = {
  draft: { label: 'Borrador', tone: 'neutral' },
  calculated: { label: 'Calculado', tone: 'info' },
  approved: { label: 'Aprobado', tone: 'success' },
  paid: { label: 'Pagado', tone: 'success' },
  closed: { label: 'Cerrado', tone: 'neutral' },
}

/**
 * Liquidaciones. Las cuatro de `Payslip.STATUS_CHOICES` — sin `closed`.
 *
 * Se derivan del catálogo del período para que los dos no puedan divergir: el
 * defecto que este archivo viene a cerrar es justamente el de dos copias que se
 * separan con el tiempo.
 */
export const PAYSLIP_STATUS: StatusCatalog = {
  draft: PAYROLL_PERIOD_STATUS.draft,
  calculated: PAYROLL_PERIOD_STATUS.calculated,
  approved: PAYROLL_PERIOD_STATUS.approved,
  paid: PAYROLL_PERIOD_STATUS.paid,
}

/**
 * Finiquitos. Las tres de `Settlement.STATUS_CHOICES`.
 *
 * ⚠️ **No tiene `approved` y no es un olvido**: un finiquito se calcula y se
 * paga, sin paso de aprobación. Por eso `TerminationsTab` declaraba tres claves
 * donde las otras pestañas de nómina declaran cuatro — ahí el mapa propio estaba
 * bien y lo que faltaba era que se notara que es otro dominio.
 */
export const SETTLEMENT_STATUS: StatusCatalog = {
  draft: PAYROLL_PERIOD_STATUS.draft,
  calculated: PAYROLL_PERIOD_STATUS.calculated,
  paid: PAYROLL_PERIOD_STATUS.paid,
}

/**
 * Solicitudes de vacaciones. Las cinco de `VacationRequest.STATUS_CHOICES`.
 *
 * ⚠️ `pending` es `warning` y no `neutral`: una solicitud sin resolver espera
 * una decisión de alguien, y hasta el #1186 el jefe **no recibía ningún aviso**
 * de que existiera. El color es lo único que la distingue en la tabla.
 *
 * ⚠️ El rótulo de `approved` es «Aprobado» y no el «Aprobado (programadas)» del
 * modelo: el paréntesis es una aclaración para el admin de Django y las tres
 * pantallas muestran la forma corta desde siempre.
 */
export const VACATION_REQUEST_STATUS: StatusCatalog = {
  pending: { label: 'Pendiente', tone: 'warning' },
  // Con la primera aprobación, esperando al segundo aprobador del tenant (#1751).
  // Sigue en `warning`: todavía espera la decisión de alguien.
  pending_second: { label: 'Pendiente 2.ª aprobación', tone: 'warning' },
  approved: { label: 'Aprobado', tone: 'success' },
  rejected: { label: 'Rechazado', tone: 'danger' },
  taken: { label: 'Gozadas', tone: 'info' },
  cancelled: { label: 'Anulada', tone: 'neutral' },
}

/**
 * Permisos y licencias. Los cinco de `LeaveRequest.STATUS_CHOICES`.
 *
 * Derivadas de las de vacaciones: son el mismo circuito de aprobación sobre otro
 * modelo, así que compartir la entrada evita que un día se pinten distinto.
 * ⚠️ Lo que **no** comparte es `taken`, que un permiso no tiene. `cancelled` va
 * con su propio rótulo en masculino («Anulado»): el de vacaciones es «Anulada».
 */
export const LEAVE_REQUEST_STATUS: StatusCatalog = {
  pending: VACATION_REQUEST_STATUS.pending,
  // Un beneficio de días que pide segundo aprobador (#1765).
  pending_second: VACATION_REQUEST_STATUS.pending_second,
  approved: VACATION_REQUEST_STATUS.approved,
  rejected: VACATION_REQUEST_STATUS.rejected,
  cancelled: { label: 'Anulado', tone: 'neutral' },
}

/**
 * Pedido de firma de un documento del trabajador. Los cuatro de
 * `SignatureRequest.STATUS_CHOICES`.
 *
 * `pending` es `warning` porque espera algo de la persona que lo mira; un
 * pedido retirado por la empresa es un cierre y va `neutral`. ⚠️ No hay
 * `expired`: el modelo no lo tiene, a propósito.
 */
export const SIGNATURE_STATUS: StatusCatalog = {
  pending: { label: 'Pendiente de firma', tone: 'warning' },
  signed: { label: 'Firmado', tone: 'success' },
  rejected: { label: 'Rechazado', tone: 'danger' },
  cancelled: { label: 'Retirado por la empresa', tone: 'neutral' },
}

/**
 * Link de pago de un documento. Los cuatro de `PaymentLink.STATUS_CHOICES`.
 *
 * Existe porque la Vista 360 mostraba la clave cruda (`active`, `expired`) en
 * la pastilla. `expired` es `warning` y no `neutral`: un link vencido que el
 * cliente todavía tiene en su correo es algo a reemitir, no un cierre.
 */
export const PAYMENT_LINK_STATUS: StatusCatalog = {
  active: { label: 'Activo', tone: 'info' },
  paid: { label: 'Pagado', tone: 'success' },
  expired: { label: 'Expirado', tone: 'warning' },
  cancelled: { label: 'Cancelado', tone: 'neutral' },
}

/**
 * Pago de una factura de compra. `SupplierInvoice.PAYMENT_STATUS_CHOICES`.
 *
 * ⚠️ `pending` es `neutral` y no `warning`, y esa decisión separa dos cosas que
 * hoy se ven iguales: una factura **pendiente con vencimiento futuro** es el
 * curso normal, y una **vencida** es la que pide atención. Pintar las dos de
 * ámbar —como hacía `SupplierPaymentsPage`— borra justamente la distinción que
 * el modelo se tomó el trabajo de guardar en `overdue`.
 *
 * ⚠️ `paid_historical` va en verde, y eso **respeta una decisión ya tomada**:
 * `SupplierPaymentsPage` lo dejó escrito en un comentario tras un bug donde caía
 * al fallback «Pendiente» y una factura ya saldada se leía como deuda viva. Lo
 * que la distingue de un pago real es el rótulo, que lo dice.
 */
export const PAYABLE_STATUS: StatusCatalog = {
  pending: { label: 'Pendiente', tone: 'neutral' },
  partial: { label: 'Pago Parcial', tone: 'info' },
  paid: { label: 'Pagado', tone: 'success' },
  overdue: { label: 'Vencido', tone: 'danger' },
  paid_historical: { label: 'Pagado (histórico)', tone: 'success' },
}

/**
 * Comprobantes contables. Las tres de `AccountingVoucher.STATUS_CHOICES`.
 *
 * 🔴 **`reversed` NO está, aunque `VoucherViewerModal` lo declaraba**: no es un
 * estado del modelo. La reversión se guarda en `reversed_by` / `reversed_at`
 * sobre el comprobante original, así que esa entrada del visor nunca se activó
 * — y la consecuencia sigue viva: **un comprobante revertido se ve igual que uno
 * vigente**, en el listado y en el visor. Agregarlo acá inventaría un estado;
 * mostrarlo es un cambio de producto aparte, anotado como pendiente.
 *
 * ⚠️ `draft` es `neutral` por coherencia con los otros seis catálogos, donde un
 * borrador nunca es alerta. `VoucherViewerModal` lo pintaba ámbar y el listado
 * gris: el mismo comprobante cambiaba de color al abrirlo.
 */
export const VOUCHER_STATUS: StatusCatalog = {
  draft: { label: 'Borrador', tone: 'neutral' },
  approved: { label: 'Aprobado', tone: 'success' },
  rejected: { label: 'Rechazado', tone: 'danger' },
}

/**
 * Terminales del POS externo. Las cuatro que calcula `TerminalSerializer.get_status`.
 *
 * `pending` es `warning` y no `neutral`: un terminal creado y nunca activado es
 * una caja que alguien espera usar y todavía no puede.
 */
export const POS_TERMINAL_STATUS: StatusCatalog = {
  pending: { label: 'Sin activar', tone: 'warning' },
  active: { label: 'Activo', tone: 'success' },
  revoked: { label: 'Revocado', tone: 'danger' },
  inactive: { label: 'Desactivado', tone: 'neutral' },
}

export const POS_FOLIO_RANGE_STATUS: StatusCatalog = {
  active: { label: 'Vigente', tone: 'success' },
  exhausted: { label: 'Agotado', tone: 'neutral' },
  revoked: { label: 'Revocado', tone: 'danger' },
}

/** Ventas recibidas de los terminales del POS (F3). */
export const POS_SALE_STATUS: StatusCatalog = {
  received: { label: 'Recibida', tone: 'info', live: true },
  signed: { label: 'Firmada', tone: 'info', live: true },
  sent: { label: 'Enviada al SII', tone: 'warning', live: true },
  accepted: { label: 'Aceptada', tone: 'success' },
  rejected: { label: 'Rechazada', tone: 'danger' },
  // El timbre no verificó o (notas de crédito) la nota no cabe en el original.
  conflict: { label: 'En conflicto', tone: 'danger' },
}

/** Si el cierre de una caja del POS ya está en la contabilidad (F5b). */
export const POS_CASH_ACCOUNTING_STATUS: StatusCatalog = {
  accounted: { label: 'Contabilizada', tone: 'success' },
  pending: { label: 'Sin contabilizar', tone: 'warning' },
  waiting_sales: { label: 'Esperando ventas', tone: 'info', live: true },
  blocked: { label: 'Ventas en conflicto', tone: 'danger' },
  not_configured: { label: 'Faltan cuentas', tone: 'warning' },
}

/** Versiones de una plantilla de impresión avanzada (PLAN_SUTEX_Y_PLANTILLAS D3). */
export const PRINT_TEMPLATE_STATUS: StatusCatalog = {
  draft: { label: 'Borrador', tone: 'neutral' },
  published: { label: 'Publicada', tone: 'success' },
  retired: { label: 'Retirada', tone: 'neutral' },
}
